import React, { useState } from "react";
import {
  VStack,
  HStack,
  Text,
  Box,
  Button,
  Badge,
  Input,
  ButtonGroup,
  Flex,
  Icon,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import useThemeColors from "../../../hooks/useThemeColors";
import useIcons from "../../../hooks/useIcons";
import Cad from "../../CustomUi/Cad";
import SectionHeader from "../../CustomUi/SectionHeader";
import { useStyles } from "../../../hooks/useStyles";
import {
  BulkPromotionRequestDto,
} from "../../../services/Dtos";

import SelectInput from "../../CustomUi/SelectInput";
import CustomPopover from "../../CustomUi/CustomPopover";
import CustomCheckBox from "../../CustomUi/CustomCheckBox";
import CustomModal from "../../CustomUi/CustomModal";
import CustomAlert from "../../CustomUi/CustomAlert";
import CustomSwitch from "../../CustomUi/CustomSwitch";
import CustomButton from "../../CustomUi/CustomButton";
import { usePromotionService } from "../../../hooks/Domain/usePromotionService";
import {
  eligibilityFilters,
} from "../../../services/constants";
import { PromotionStatus } from "../../../services/Enums";

const PromotionEligibilityPage: React.FC = () => {
  const { classes, levels, school } = useSchoolsContext();
  const colors = useThemeColors();
  const icons = useIcons();
  const { scrollBar } = useStyles();
  const {
    eligibilityData,
    getPromotionStatusInfo,
    isLoading,
    selectedStudents,
    setSelectedStudents,
    promoteStudents,
    getEligibilityStatusColor,
    getEligibilityIcon,
    getEligibilityText,
    selectedClass,
    setSelectedClass,
    selectedLevel,
    setSelectedLevel,
    searchTerm,
    setSearchTerm,
    eligibilityFilter,
    setEligibilityFilter,
    promotionStatusFilter,
    setPromotionStatusFilter,
    promotionLoading,
    currentSession,
    loadEligibilityData,
    filteredEligibility,
    handleSelectAll,
    handleDeselectAll,
    canMarkForPromotion,
    handleStudentSelection,
  } = usePromotionService();

  const {
    open: isPromotionModalOpen,
    onOpen: onPromotionModalOpen,
    onClose: onPromotionModalClose,
  } = useDisclosure();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Load eligibility data
  const fetchEligibilityData = async () => {
    if (currentSession?.id) {
      await loadEligibilityData(selectedClass);
    }
  };

  // Handle promotion
  const handlePromoteStudents = async () => {
    const request: BulkPromotionRequestDto = {
      sessionId: currentSession?.id || "",
      studentIds: selectedStudents,
      autoPromote: false,
    };

    promoteStudents(request);
    onPromotionModalClose();
  };

  return (
    <VStack gap={{ base: 2, md: 4 }}>
      {/* Controls */}
      <Cad
        position={"sticky"}
        top={-4}
        zIndex={2}
        bg={colors.background.primary}
      >
        <VStack gap={4} align="stretch">
          <SectionHeader
            title="Promotion Eligibility"
            subtitle={`Check student eligibility and mark them for promotion. Marked promotions require admin approval in the Promotion History tab. Current Session: ${
              currentSession?.sessionName || "Not Set"
            }`}
            icon={icons.main.promotion}
          />

          {/* Search and Filters */}
          <HStack gap={3} flexWrap={"wrap"} alignItems="flex-start">
            {filteredEligibility.length > 0 && (
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                flex={{ base: "1", md: "1" }}
                minW={{ base: "200px", md: "250px" }}
              />
            )}

            <CustomPopover
              setOpen={setIsFilterOpen}
              open={isFilterOpen}
              w={"320px"}
              header={<Text fontWeight="semibold">Eligibility Filters</Text>}
              trigger={
                <Button
                  size="sm"
                  variant="surface"
                  colorPalette={colors.schoolAccent.primary}
                >
                  <Icon as={icons.utility.filter} />
                  Check Eligibility
                </Button>
              }
            >
              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Class
                  </Text>
                  <SelectInput
                    placeholder={"All Classes"}
                    label={""}
                    name={""}
                    value={selectedClass}
                    onChange={(e) => {
                      setSelectedClass(e.target.value);
                      // Clear selection when class changes
                      setSelectedStudents([]);
                    }}
                    collection={[
                      { label: "All Classes", value: "" },
                      ...classes.map((cls) => ({
                        label: cls.className,
                        value: cls.id,
                      })),
                    ]}
                    size="sm"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Level
                  </Text>
                  <SelectInput
                    placeholder={"All Levels"}
                    label={""}
                    name={""}
                    value={selectedLevel}
                    onChange={(e) => {
                      setSelectedLevel(e.target.value);
                      // Clear selection when level changes
                      setSelectedStudents([]);
                    }}
                    collection={[
                      { label: "All Levels", value: "" },
                      ...levels.map((lv) => ({
                        label: lv.levelName,
                        value: lv.id,
                      })),
                    ]}
                    size="sm"
                    disabled={selectedClass !== ""}
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Eligibility Status
                  </Text>
                  <SelectInput
                    placeholder={"Eligibility Status"}
                    label={""}
                    name={""}
                    value={eligibilityFilter}
                    onChange={(e) =>
                      setEligibilityFilter(e.target.value as any)
                    }
                    collection={eligibilityFilters}
                    size="sm"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Show Only Students Available for Promotion
                  </Text>
                  <CustomSwitch
                    checked={
                      promotionStatusFilter === PromotionStatus.Available
                    }
                    onChange={() =>
                      setPromotionStatusFilter(
                        promotionStatusFilter === PromotionStatus.Available
                          ? "all"
                          : PromotionStatus.Available
                      )
                    }
                    label="Show only students available for promotion"
                    size="sm"
                  />
                </Box>

                <CustomButton
                  size="sm"
                  icon={icons.utility.refresh}
                  label="Fetch Eligibility"
                  onClick={() => {
                    fetchEligibilityData();
                    setIsFilterOpen(false);
                  }}
                  loading={isLoading}
                  disabled={!currentSession || !school?.leapTermId}
                  tooltipContent={
                    !currentSession
                      ? "No active session found. Please set a current session for the school."
                      : !school?.leapTermId
                      ? "No leap term found. Please set a leap term for the school."
                      : ""
                  }
                />
              </VStack>
            </CustomPopover>

            {filteredEligibility.length > 0 && (
              <ButtonGroup size="sm" flexShrink={0}>
                <Button onClick={handleSelectAll} variant="outline">
                  Select All Available
                </Button>
                <Button onClick={handleDeselectAll} variant="outline">
                  Clear Selection
                </Button>
              </ButtonGroup>
            )}
            {/* Promotion Actions */}
            {selectedStudents.length > 0 && (
              <Button
                colorPalette={colors.schoolAccent.primary}
                onClick={onPromotionModalOpen}
                size="sm"
              >
                <Icon as={icons.main.promotion} />
                Mark Selected for Promotion
              </Button>
            )}
          </HStack>

          {/* Summary Stats */}
          {eligibilityData.length > 0 && (
            <HStack gap={4} wrap="wrap">
              <Badge colorPalette="blue" variant="subtle">
                Total: {eligibilityData.length}
              </Badge>
              <Badge variant="subtle">
                Eligible:{" "}
                {eligibilityData.filter((item) => item.isEligible).length}
              </Badge>
              <Badge variant="subtle">
                Ineligible:{" "}
                {eligibilityData.filter((item) => !item.isEligible).length}
              </Badge>
              <Badge variant="subtle">
                Available:{" "}
                {
                  eligibilityData.filter((item) => canMarkForPromotion(item))
                    .length
                }
              </Badge>
              <Badge variant="subtle">
                Pending:{" "}
                {
                  eligibilityData.filter((item) => item.hasPendingPromotion)
                    .length
                }
              </Badge>
              <Badge variant="subtle">
                Approved:{" "}
                {
                  eligibilityData.filter((item) => item.hasApprovedPromotion)
                    .length
                }
              </Badge>
              <Badge colorPalette="red" variant="subtle">
                Rejected:{" "}
                {
                  eligibilityData.filter((item) => item.hasRejectedPromotion)
                    .length
                }
              </Badge>
              {(() => {
                const selectedIneligibleCount = eligibilityData.filter(
                  (item) =>
                    selectedStudents.includes(item.studentId) &&
                    !item.isEligible
                ).length;
                if (selectedIneligibleCount > 0) {
                  return (
                    <Badge colorPalette="orange" variant="subtle">
                      Ineligible Selected: {selectedIneligibleCount}
                    </Badge>
                  );
                }
                return null;
              })()}
              <Badge colorPalette="green" variant="solid">
                Total Selected: {selectedStudents.length}
              </Badge>
            </HStack>
          )}
        </VStack>
      </Cad>

      {/* Eligibility List */}
      <Cad>
        {isLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="lg" color={colors.schoolAccent.primary} />
          </Flex>
        ) : eligibilityData.length === 0 ? (
          <Box textAlign="center" py={8} color={colors.text.secondary}>
            <Icon as={icons.people.student} boxSize={8} mb={2} opacity={0.5} />
            <Text fontSize="sm">
              {currentSession
                ? "Use the filters above and click 'Fetch Eligibility' to check student eligibility."
                : "No active session found. Please set a current session for the school."}
            </Text>
            {currentSession && (
              <Text fontSize="xs" mt={1}>
                Current Session: {currentSession.sessionName}
              </Text>
            )}
          </Box>
        ) : filteredEligibility.length === 0 ? (
          <Box textAlign="center" py={8} color={colors.text.secondary}>
            <Icon as={icons.people.students} boxSize={8} mb={2} opacity={0.5} />
            <Text fontSize="sm">No students match the current filters.</Text>
            <Text fontSize="xs" mt={1}>
              Try adjusting your search terms or filter criteria.
            </Text>
          </Box>
        ) : (
          <Box css={scrollBar}>
            <VStack align="stretch" gap={2}>
              {filteredEligibility.map((item) => {
                const EligibilityIcon = getEligibilityIcon(item.isEligible);
                const currentClass = classes.find(
                  (c) => c.id === item.currentClassId
                );
                const nextClass = classes.find(
                  (c) => c.id === item.nextClassId
                );

                return (
                  <Box
                    key={item.studentId}
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={
                      getPromotionStatusInfo(item)
                        ? getPromotionStatusInfo(item)!.borderColor
                        : colors.border.subtle
                    }
                    bg={
                      getPromotionStatusInfo(item)
                        ? getPromotionStatusInfo(item)!.bg
                        : colors.background.secondary
                    }
                    opacity={getPromotionStatusInfo(item) ? 0.4 : 1}
                    transition="all 0.2s"
                    _hover={{
                      bg: getPromotionStatusInfo(item)
                        ? getPromotionStatusInfo(item)!.color + "/10"
                        : colors.schoolAccent.hover,
                    }}
                  >
                    <Flex justify="space-between" align="start" mb={3}>
                      <HStack gap={3}>
                        <CustomCheckBox
                          checked={selectedStudents.includes(item.studentId)}
                          label={""}
                          onChange={() =>
                            handleStudentSelection(item.studentId)
                          }
                        />
                        <VStack align="start" gap={1}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {item.studentName}
                          </Text>
                          <Text fontSize="xs" color={colors.text.secondary}>
                            {currentClass?.className} →{" "}
                            {nextClass?.className || "Graduation"}
                          </Text>
                        </VStack>
                      </HStack>

                      <VStack align="end" gap={2}>
                        {/* Eligibility Status */}
                        <Badge
                          colorPalette={getEligibilityStatusColor(
                            item.isEligible
                          )}
                          variant="subtle"
                          fontSize="xs"
                        >
                          <HStack gap={1}>
                            <EligibilityIcon size={10} />
                            <Text>{getEligibilityText(item.isEligible)}</Text>
                          </HStack>
                        </Badge>

                        {/* Promotion Status */}
                        {getPromotionStatusInfo(item) && (
                          <Badge
                            colorPalette={getPromotionStatusInfo(item)!.color}
                            variant="solid"
                            fontSize="xs"
                          >
                            <HStack gap={1}>
                              <Icon as={getPromotionStatusInfo(item)!.icon} />
                              <Text>{getPromotionStatusInfo(item)!.label}</Text>
                            </HStack>
                          </Badge>
                        )}
                      </VStack>
                    </Flex>

                    {/* Performance Metrics */}
                    <HStack gap={4} mb={3} wrap="wrap">
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Average Score
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {item.averageScore.toFixed(1)}% (Min:{" "}
                          {item.minimumRequiredScore}%)
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Position
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {item.positionInClass}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Attendance
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {item.attendancePercentage.toFixed(1)}%
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Subjects Passed
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {item.allSubjectsPassed ? "All" : "Some Failed"}
                        </Text>
                      </Box>
                    </HStack>

                    {/* Ineligibility Reasons */}
                    {!item.isEligible && item.reasons.length > 0 && (
                      <Box
                        p={2}
                        bg={colors.status.errorBg}
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderLeftColor="red.500"
                      >
                        <Text fontSize="xs" color="red.600" fontWeight="medium">
                          Reasons for ineligibility:
                        </Text>
                        <VStack align="start" gap={1} mt={1}>
                          {item.reasons.map((reason, index) => (
                            <Text key={index} fontSize="xs" color="red.600">
                              • {reason}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        )}
      </Cad>

      {/* Promotion Confirmation Modal */}
      <CustomModal
        isOpen={isPromotionModalOpen}
        onClose={onPromotionModalClose}
        handleClose={onPromotionModalClose}
        handleAction={handlePromoteStudents}
        isLoading={promotionLoading}
        hasChanges={selectedStudents.length > 0}
        title="Confirm Promotion Marking"
        icon={icons.main.promotion}
        actionText="Mark for Promotion"
      >
        <VStack gap={4} align="stretch">
          <CustomAlert
            status="info"
            title="Promotion Workflow"
            message="Students will be marked for promotion with 'Pending' status. They will require admin approval before being officially promoted to their next class."
          />

          {/* Warning for ineligible students */}
          {(() => {
            const selectedIneligibleStudents = eligibilityData.filter(
              (item) =>
                selectedStudents.includes(item.studentId) && !item.isEligible
            );
            const ineligibleCount = selectedIneligibleStudents.length;

            if (ineligibleCount > 0) {
              return (
                <CustomAlert
                  status="warning"
                  title="Ineligible Students Selected"
                  message={`${ineligibleCount} of the selected students do not meet the promotion criteria. You are making an exception to mark them for promotion despite their ineligibility.`}
                />
              );
            }
            return null;
          })()}

          <Text fontSize="sm">
            <strong>Students to Mark:</strong> {selectedStudents.length}
          </Text>
          <Text fontSize="sm">
            <strong>Session:</strong> {currentSession?.sessionName || "Not Set"}
          </Text>
          <Text fontSize="sm" color={colors.text.secondary}>
            <strong>Next Step:</strong> After marking, these promotions will
            appear in the "Promotion History" tab for review and approval.
          </Text>
        </VStack>
      </CustomModal>
    </VStack>
  );
};

export default PromotionEligibilityPage;
